import React, { useEffect, useMemo, useState } from 'react';
import { abilitiesAPI } from '../../services/api';
import './CharacterSheetModal.css';

import {
  buildAbilityLookups,
  computeDerivedCombatStats,
  safeParseJson,
} from '../../utils/derivedStats';

function CharacterSheetModal({ isOpen, character, onClose }) {
  const sheet = useMemo(() => {
    if (!character) return null;

    // Normalize: allow nested stats or flat stats
    const c = character || {};
    const s = c.stats || {};

    return {
      ...c,
      physique: c.physique ?? s.physique,
      coordination: c.coordination ?? s.coordination,
      logic: c.logic ?? s.logic,
      perception: c.perception ?? s.perception,
      presence: c.presence ?? s.presence,
      resilience: c.resilience ?? s.resilience,
      characterType: c.characterType ?? c.aetherionType,
    };
  }, [character]);

  const featsObj = useMemo(() => (sheet ? safeParseJson(sheet.feats, {}) : {}), [sheet]);
  const forgesObj = useMemo(() => (sheet ? safeParseJson(sheet.forges, {}) : {}), [sheet]);
  const equipmentObj = useMemo(() => (sheet ? safeParseJson(sheet.equipment, {}) : {}), [sheet]);

  const featsEntries = useMemo(
    () => Object.entries(featsObj || {}).sort(([a], [b]) => a.localeCompare(b)),
    [featsObj]
  );
  const forgesEntries = useMemo(
    () => Object.entries(forgesObj || {}).sort(([a], [b]) => a.localeCompare(b)),
    [forgesObj]
  );
  const equipmentEntries = useMemo(
    () => Object.entries(equipmentObj || {}).sort(([a], [b]) => a.localeCompare(b)),
    [equipmentObj]
  );

  // Ability definitions -> lookups
  const [abilityByCode, setAbilityByCode] = useState(null);
  const [abilityByName, setAbilityByName] = useState(null);
  const [isLoadingAbilityDefs, setIsLoadingAbilityDefs] = useState(false);

  useEffect(() => {
    let alive = true;

    if (!isOpen || !sheet) return undefined;
    if (abilityByCode && abilityByName) return undefined;

    (async () => {
      try {
        setIsLoadingAbilityDefs(true);
        const res = await abilitiesAPI.getAll();
        const list = res?.data?.abilities || [];
        const { byCode, byName } = buildAbilityLookups(list);

        if (alive) {
          setAbilityByCode(byCode);
          setAbilityByName(byName);
        }
      } catch (e) {
        console.error('Failed to load ability definitions:', e);
        if (alive) {
          setAbilityByCode(new Map());
          setAbilityByName(new Map());
        }
      } finally {
        if (alive) setIsLoadingAbilityDefs(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isOpen, sheet, abilityByCode, abilityByName]);

  const [showBreakdown, setShowBreakdown] = useState(false);

  const derived = useMemo(() => {
    if (!sheet) return null;

    return computeDerivedCombatStats({
      stats: {
        physique: sheet.physique,
        coordination: sheet.coordination,
        perception: sheet.perception,
        resilience: sheet.resilience,
      },
      feats: featsObj,
      forges: forgesObj,
      abilityByCode,
      abilityByName,
      includeBreakdown: showBreakdown,
    });
  }, [sheet, featsObj, forgesObj, abilityByCode, abilityByName, showBreakdown]);

  if (!isOpen || !sheet) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content character-sheet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{sheet.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="character-sheet-content">
            <div className="sheet-section">
              <h3>Basic Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Character Type:</span>
                  <span className="info-value">{sheet.characterType || sheet.aetherionType}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Level:</span>
                  <span className="info-value">{sheet.level || 1}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total XP:</span>
                  <span className="info-value">{sheet.totalXp || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Available XP:</span>
                  <span className="info-value">{sheet.availableXp || 0}</span>
                </div>
              </div>
            </div>

            <div className="sheet-section">
              <h3>Attributes</h3>
              <div className="info-grid">
                <div className="info-item"><span className="info-label">Physique:</span><span className="info-value">{sheet.physique}</span></div>
                <div className="info-item"><span className="info-label">Coordination:</span><span className="info-value">{sheet.coordination}</span></div>
                <div className="info-item"><span className="info-label">Logic:</span><span className="info-value">{sheet.logic}</span></div>
                <div className="info-item"><span className="info-label">Perception:</span><span className="info-value">{sheet.perception}</span></div>
                <div className="info-item"><span className="info-label">Presence:</span><span className="info-value">{sheet.presence}</span></div>
                <div className="info-item"><span className="info-label">Resilience:</span><span className="info-value">{sheet.resilience}</span></div>
              </div>
            </div>

            <div className="sheet-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                <h3 style={{ margin: 0 }}>Combat Stats</h3>
                <button
                  type="button"
                  onClick={() => setShowBreakdown((v) => !v)}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 10,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    color: 'inherit',
                    opacity: 0.9
                  }}
                >
                  {showBreakdown ? 'Hide breakdown' : 'Show breakdown'}
                </button>
              </div>

              <div className="info-grid" style={{ marginTop: 10 }}>
                <div className="info-item">
                  <span className="info-label">Max HP:</span>
                  <span className="info-value">{derived?.final?.maxHp ?? '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Movement:</span>
                  <span className="info-value">{derived?.final?.movement ?? '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Initiative:</span>
                  <span className="info-value">{derived?.final?.initiative ?? '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Evasion:</span>
                  <span className="info-value">{derived?.final?.evasion ?? '—'}</span>
                </div>
              </div>

              {isLoadingAbilityDefs ? (
                <p className="hint" style={{ marginTop: 8 }}>Loading passive modifiers…</p>
              ) : null}

              {showBreakdown && (
                <div style={{
                  marginTop: 12,
                  background: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 10,
                  padding: 12
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, opacity: 0.95 }}>Derived breakdown</div>

                  <div style={{ fontSize: '0.92rem', opacity: 0.9 }}>
                    <div><strong>Base Max HP:</strong> {derived?.base?.maxHp}</div>
                    <div><strong>HP mods:</strong> +{derived?.mods?.maxHpAdd ?? 0}, ×{derived?.mods?.maxHpMul ?? 1}</div>

                    <div style={{ marginTop: 6 }}><strong>Base Movement raw:</strong> {derived?.base?.movementRaw}</div>
                    <div><strong>Move mods:</strong> +{derived?.mods?.movementAdd ?? 0}, ×{derived?.mods?.movementMul ?? 1}</div>

                    <div style={{ marginTop: 6 }}><strong>Base Init bonus:</strong> {derived?.base?.initiativeBonus}</div>
                    <div><strong>Init mods:</strong> +{derived?.mods?.initiativeBonusAdd ?? 0}</div>

                    <div style={{ marginTop: 6 }}><strong>Evasion die:</strong> {derived?.base?.evasionDie}</div>
                    <div><strong>Evasion mods:</strong> +{derived?.mods?.evasionBonusAdd ?? 0}</div>
                  </div>

                  {(derived?.breakdown || []).length ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, opacity: 0.95 }}>Sources</div>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {derived.breakdown.map((s, idx) => (
                          <li key={`${s.source}-${idx}`} style={{ marginBottom: 4 }}>
                            <strong>{s.source}</strong>: {s.changes.join(', ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, opacity: 0.85, fontStyle: 'italic' }}>
                      No passive mechanics applied.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sheet-section">
              <h3>Skills</h3>
              {featsEntries.length === 0 ? (
                <p className="hint">No skills recorded.</p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {featsEntries.map(([skillName, data]) => {
                    const tier = Number(data?.tier ?? 0);
                    const spec = data?.specialization;
                    const specLabel = spec?.name ? `${spec.name} (Tier ${Number(spec?.tier ?? 0)})` : null;

                    return (
                      <div
                        key={skillName}
                        style={{
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 10,
                          padding: 12,
                          background: 'rgba(0,0,0,0.20)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <strong>{skillName}</strong>
                          <span style={{ opacity: 0.9 }}>Tier {tier}</span>
                        </div>
                        {specLabel && (
                          <div style={{ marginTop: 6, fontSize: '0.92rem', opacity: 0.9 }}>
                            Specialization: <strong>{specLabel}</strong>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="sheet-section">
              <h3>Abilities</h3>
              {forgesEntries.length === 0 ? (
                <p className="hint">No abilities recorded.</p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {forgesEntries.map(([abilityName, data]) => {
                    const tier = Number(data?.tier ?? 1) || 1;
                    const type = data?.type || null;
                    const xpCost = data?.xpCost ?? null;

                    return (
                      <div
                        key={abilityName}
                        style={{
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 10,
                          padding: 12,
                          background: 'rgba(0,0,0,0.20)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <strong>{abilityName}</strong>
                          <span style={{ opacity: 0.9 }}>
                            {type ? `${type}` : ''}{type ? ' • ' : ''}Tier {tier}
                            {xpCost !== null ? ` • Cost ${xpCost} XP` : ''}
                          </span>
                        </div>

                        {data?.description && (
                          <div style={{ marginTop: 6, fontSize: '0.95rem', opacity: 0.95 }}>
                            {data.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="sheet-section">
              <h3>Equipment</h3>
              {equipmentEntries.length === 0 ? (
                <p className="hint">No equipment recorded.</p>
              ) : (
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 10,
                    padding: 12,
                    margin: 0
                  }}
                >
                  {formatMaybe(equipmentObj)}
                </pre>
              )}
            </div>

            {sheet.notes && (
              <div className="sheet-section">
                <h3>Notes</h3>
                <p className="notes-text">{sheet.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatMaybe(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export default CharacterSheetModal;
